import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, CircleIcon } from 'lucide-react';
interface TreatmentJourneyProps {
  segment: string;
  onContinue: () => void;
}
const journeyStages = [{
  stage: 'Awareness',
  influence: 'Low',
  description: 'Relies on peer recommendations and conference data',
  active: false
}, {
  stage: 'Consideration',
  influence: 'High',
  description: 'Deep evaluation of clinical evidence and trial outcomes',
  active: true
}, {
  stage: 'Trial',
  influence: 'High',
  description: 'Selective patient profiling for initial prescribing',
  active: true
}, {
  stage: 'Adoption',
  influence: 'Medium',
  description: 'Gradual expansion based on real-world outcomes',
  active: false
}];
export function TreatmentJourney({
  segment,
  onContinue
}: TreatmentJourneyProps) {
  return <div className="w-full min-h-screen flex items-start justify-center bg-[#FAFAF8] px-6 py-16 overflow-y-auto" data-id="element-264">
      <div className="max-w-xl w-full space-y-6" data-id="element-265">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-2" data-id="element-266">
          <span className="inline-block px-3 py-1 bg-teal-100 text-teal-800 text-xs font-bold rounded-full uppercase tracking-wide" data-id="element-267">
            Treatment journey
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1C1917]" data-id="element-268">
            Where you influence decisions
          </h2>
          <p className="text-sm text-[#57534E]" data-id="element-269">
            Evidence Optimizers have the strongest impact at specific stages
          </p>
        </motion.div>

        <div className="space-y-3" data-id="element-270">
          {journeyStages.map((stage, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.08 * index
        }} className={`bg-white rounded-2xl p-5 shadow-sm border flex items-start gap-4 ${stage.active ? 'border-teal-200' : 'border-neutral-100'}`} data-id="element-271">
              <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${stage.active ? 'bg-teal-600 text-white' : 'bg-neutral-100 text-neutral-400'}`} data-id="element-272">
                {stage.active ? <CheckIcon className="w-5 h-5" data-id="element-273" /> : <CircleIcon className="w-4 h-4" data-id="element-274" />}
              </div>
              <div className="flex-1" data-id="element-275">
                <div className="flex items-center gap-2 mb-1" data-id="element-276">
                  <h3 className="text-base font-bold text-[#1C1917]" data-id="element-277">
                    {stage.stage}
                  </h3>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${stage.influence === 'High' ? 'bg-teal-100 text-teal-700' : stage.influence === 'Medium' ? 'bg-indigo-100 text-indigo-700' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-278">
                    {stage.influence}
                  </span>
                </div>
                <p className="text-sm text-[#57534E]" data-id="element-279">{stage.description}</p>
              </div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-teal-50 rounded-2xl p-5 border border-teal-100" data-id="element-280">
          <p className="text-sm text-teal-900 font-medium leading-relaxed" data-id="element-281">
            Evidence Optimizers are slower to adopt but become{' '}
            <strong data-id="element-282">strong advocates</strong> once convinced by data. Your
            influence peaks during Consideration and Trial.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="flex justify-center pt-4 pb-20" data-id="element-283">
          <button onClick={onContinue} className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#1C1917] text-white font-bold rounded-xl hover:bg-neutral-800 transition-all shadow-md hover:-translate-y-0.5" data-id="element-284">
            Continue
            <ArrowRightIcon className="w-5 h-5" data-id="element-285" />
          </button>
        </motion.div>
      </div>
    </div>;
}